import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Play, Pause, RotateCw, CheckSquare, ArrowRight, AlertTriangle, XCircle, CheckCircle2, ShieldAlert, Gauge, Clock } from "lucide-react";

const ETAT_STYLE = {
  BROUILLON: { c: "#6B7280", bg: "#6B72801a", txt: "Brouillon" },
  A_PREPARER: { c: "#B45309", bg: "#B453091a", txt: "À préparer" },
  SOUS_CONDITIONS: { c: "#D97706", bg: "#D977061a", txt: "Sous conditions — démarrage non autorisé" },
  PRETE: { c: "#15803D", bg: "#15803D1a", txt: "Prête à démarrer" },
  EN_COURS: { c: "#0E7490", bg: "#0E74901a", txt: "En cours" },
  SUSPENDUE: { c: "#C2410C", bg: "#C2410C1a", txt: "Suspendue" },
  TERMINEE: { c: "#374151", bg: "#3741511a", txt: "Terminée" },
};
const TR = {
  demarrer: { label: "Démarrer l'intervention", icon: Play, cls: "btn-p" },
  suspendre: { label: "Suspendre", icon: Pause, cls: "btn-g" },
  reprendre: { label: "Reprendre", icon: RotateCw, cls: "btn-p" },
  terminer: { label: "Terminer l'intervention", icon: CheckSquare, cls: "btn-g" },
};

export default function DecisionIntervention({ operationId, canEdit, onGoto, onChanged }) {
  const [e, setE] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/operations/${operationId}/etat`).then((r) => setE(r.data)).catch(() => {});
  }, [operationId]);
  useEffect(load, [load]);

  const transition = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/operations/${operationId}/transition`, { action });
      setE(data);
      toast.success(`Intervention : ${ETAT_STYLE[data.etat]?.txt || data.etatLabel}`);
      onChanged && onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.msg : apiErr(err));
    } finally { setBusy(false); }
  };

  if (!e) return null;
  const st = ETAT_STYLE[e.etat] || ETAT_STYLE.BROUILLON;
  const pa = e.prochaineAction || {};

  return (
    <div className="card" data-testid="decision-intervention" style={{ borderLeft: `5px solid ${st.c}`, background: st.bg }}>
      {/* NIVEAU 1 — ÉTAT + niveau de préparation */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <ShieldAlert size={22} color={st.c} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: ".72rem", letterSpacing: ".08em", color: "var(--text-muted)", textTransform: "uppercase" }}>État de l'intervention</div>
          <div data-testid="di-etat" data-etat={e.etat} style={{ fontSize: "1.4rem", fontWeight: 800, color: st.c, lineHeight: 1.15 }}>{st.txt}</div>
        </div>
        <div style={{ textAlign: "right", minWidth: 150 }}>
          <div style={{ fontSize: ".72rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}><Gauge size={13} />Niveau de préparation</div>
          <div style={{ fontSize: "1.4rem", fontWeight: 800 }} data-testid="di-progression">{e.progression}%</div>
        </div>
      </div>
      <div style={{ height: 6, background: "var(--surface-2)", borderRadius: 6, overflow: "hidden", margin: "8px 0 4px" }}>
        <div style={{ width: `${e.progression}%`, height: "100%", background: st.c, transition: "width .4s" }} />
      </div>
      {/* Alerte temporelle = information SECONDAIRE (ne concurrence pas les conditions bloquantes) */}
      {e.alerteDate && (
        <div data-testid="di-alerte-date" style={{ fontSize: ".74rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
          <Clock size={12} />{e.alerteDate}
        </div>
      )}

      {/* NIVEAU 2 — BLOQUANTS (ce qui empêche le démarrage) */}
      {e.bloquants.length > 0 && (
        <div data-testid="di-bloquants" className="card" style={{ padding: "10px 12px", margin: "8px 0", background: "var(--surface)", borderLeft: "3px solid #DC2626" }}>
          <div style={{ fontWeight: 700, color: "#DC2626", display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <XCircle size={16} />{e.bloquants.length} point{e.bloquants.length > 1 ? "s" : ""} empêche{e.bloquants.length > 1 ? "nt" : ""} le démarrage
          </div>
          {e.bloquants.map((b) => (
            <div key={b.code} data-testid={`di-bloquant-${b.code}`} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "7px 0", borderTop: "1px solid var(--border)" }}>
              <span style={{ color: "#DC2626" }}>🔴</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: ".86rem" }}>{b.titre}</div>
                <div style={{ fontSize: ".76rem", color: "var(--text-muted)" }}>{b.explication}</div>
              </div>
              {canEdit && <button className="btn btn-p btn-sm" data-testid={`di-fix-${b.code}`} onClick={() => onGoto && onGoto(b.ongletIndex ?? 0)} style={{ whiteSpace: "nowrap" }}>{b.cta}</button>}
            </div>
          ))}
        </div>
      )}

      {/* NIVEAU 3 — PROCHAINE ACTION + ACTION (premier niveau) */}
      <div className="card" data-testid="di-prochaine-action" style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0", padding: "12px 14px", background: st.bg, border: `1px solid ${st.c}55` }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: ".7rem", letterSpacing: ".08em", color: st.c, textTransform: "uppercase", fontWeight: 700 }}>Prochaine action</div>
          <div style={{ fontWeight: 700 }}>{pa.libelle}</div>
        </div>
        {pa.cta && pa.code !== "demarrer" && pa.code !== "suivre" && (
          <button className="btn btn-p" data-testid="di-next-cta" onClick={() => onGoto && onGoto(pa.ongletIndex ?? 0)}>{pa.cta}<ArrowRight size={14} /></button>
        )}
      </div>

      {/* NIVEAU 4 — Secondaire : points d'attention + contrôles satisfaits */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginTop: 4 }}>
        {e.avertissements.length > 0 && (
          <div data-testid="di-avertissements">
            <div style={{ fontWeight: 700, color: "#D97706", display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><AlertTriangle size={15} />{e.avertissements.length} à vérifier</div>
            {e.avertissements.map((a) => (
              <div key={a.code} data-testid={`di-avert-${a.code}`} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0", borderTop: "1px solid var(--border)" }}>
                <span>🟠</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: ".82rem" }}>{a.titre}</div>
                  <div style={{ fontSize: ".72rem", color: "var(--text-muted)" }}>{a.explication}</div>
                </div>
                {canEdit && <button className="btn btn-g btn-sm" onClick={() => onGoto && onGoto(a.ongletIndex ?? 0)}>{a.cta}</button>}
              </div>
            ))}
          </div>
        )}
        <div data-testid="di-satisfaits" style={{ alignSelf: "start" }}>
          <div style={{ fontWeight: 700, color: "#15803D", display: "flex", alignItems: "center", gap: 6 }}><CheckCircle2 size={15} />{e.satisfaits} contrôle{e.satisfaits > 1 ? "s" : ""} satisfait{e.satisfaits > 1 ? "s" : ""}</div>
          <div style={{ fontSize: ".76rem", color: "var(--text-muted)", marginTop: 4 }}>
            PDP {e.pdpGenere ? (e.pdpSigne ? "généré et signé ✓" : "généré (à signer)") : (e.pdpGenerable ? "générable" : "pas encore générable")} · Permis {e.permis.valides.length}/{e.permis.requis.length} validés · Coactivité : {e.coactivite.conflits} détectée(s)
          </div>
        </div>
      </div>

      {/* Transitions manuelles gardées */}
      {canEdit && Object.entries(e.transitions).some(([, v]) => v) && (
        <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap", borderTop: "1px solid var(--border)", paddingTop: 12 }}>
          {["demarrer", "reprendre", "suspendre", "terminer"].filter((a) => e.transitions[{ demarrer: "peutDemarrer", suspendre: "peutSuspendre", reprendre: "peutReprendre", terminer: "peutTerminer" }[a]]).map((a) => {
            const T = TR[a]; const Ic = T.icon;
            return <button key={a} className={`btn ${T.cls}`} disabled={busy} data-testid={`di-transition-${a}`} onClick={() => transition(a)}><Ic size={14} />{T.label}</button>;
          })}
        </div>
      )}
    </div>
  );
}
